/**
 * Caller identity helpers.
 *
 * The alias itself comes from the API Gateway authorizer via `GET /me` — the
 * frontend deliberately does not decode the Midway token, because a claim read
 * in the browser is unverified and the backend already verified the signature.
 */

/**
 * Amazon aliases are short lowercase tokens. Anything else means something
 * upstream is wrong, and we would rather hide the identity chip than render an
 * unexpected value as a person's name or paste it into a URL.
 */
const ALIAS_PATTERN = /^[a-z0-9][a-z0-9._-]{1,63}$/i;

export function isValidAlias(alias?: string | null): boolean {
  return typeof alias === 'string' && ALIAS_PATTERN.test(alias.trim());
}

/**
 * Badge Photo Service. `badgephotos.corp.amazon.com` is the current host — the
 * old `internal-cdn.amazon.com/badgephotos...` path is deprecated. No
 * `give404ifmissing` parameter: the service's own placeholder renders better in
 * a nav slot than a broken-image icon would.
 */
export function badgePhotoUrl(alias: string): string {
  return `https://badgephotos.corp.amazon.com/?uid=${encodeURIComponent(alias)}`;
}

export function phonetoolUrl(alias: string): string {
  return `https://phonetool.amazon.com/users/${encodeURIComponent(alias)}`;
}

/**
 * Monogram shown when the badge photo cannot be loaded.
 *
 * This is not an edge case: the photo lives behind Midway on another host, so
 * whether it loads at all depends on browser session state this app does not
 * control. Separators are skipped so the result is always alphanumeric — "J."
 * reads as a rendering fault rather than a name.
 */
export function initials(alias: string): string {
  return (alias.match(/[a-z0-9]/gi) ?? [])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
